/* ==========================================================================
   cbg-cart-sync.js — the single place that re-renders the header cart UI
   after any cart mutation, with no page reload.

   WHY THIS FILE EXISTS
   --------------------
   The CBG add-to-cart paths (PDP "ADD TO CART" popup in section-cbg-product,
   collection "Quick view" modal in cbg-quick-view.js) POST to routes.cart_add_url
   and then only published Dawn's `cart-update` pub/sub event. Two things were
   missing, so the header only ever caught up on a full page load:

     1. The count bubble (#cart-icon-bubble .cart-count-bubble) is server-rendered
        Liquid. Dawn only ever refreshes it from a Cart Ajax `sections` response
        (see cart.js / cart-notification.js getSectionsToRender), and the CBG add
        requests never asked for `sections` — so nothing updated the counter.

     2. The mini-cart dropdown body DID subscribe to `cart-update`, but from the
        inline <script> inside snippets/cbg-mini-cart.liquid. That inline script
        runs while the <body> is still parsing, which is BEFORE the deferred
        constants.js / pubsub.js in <head> have executed. `subscribe` was still
        undefined at that moment, the `typeof subscribe !== 'undefined'` guard
        silently skipped registration, and the panel was never wired up at all.

   HOW IT WORKS
   ------------
   This is a deferred external script loaded from layout/theme.liquid AFTER
   constants.js / pubsub.js, so the pub/sub globals are guaranteed to exist here.

     - sectionsParam()        the Section Rendering API ids the header needs:
                              `cart-icon-bubble` (the counter) + the header
                              section itself (which contains the dropdown body).
     - handleCartResponse()   called by the CBG add-to-cart paths with the parsed
                              /cart/add response. When that response carries
                              `sections` (because sectionsParam() was sent with
                              the request) the header is rendered from it in the
                              SAME round trip — instant, no extra request. It then
                              re-publishes `cart-update` so the rest of the theme
                              (cart page, price-per-item, recipient form, …) still
                              reacts exactly as before.
     - It also subscribes to `cart-update` itself, so mutations coming from
       anywhere else — cart page quantity edits (cart.js), cbg-cart.js promo /
       quick-add / save-for-later, quick-order-list, apps — refresh the header too.
       Those payloads have no `sections`, so refresh() fetches them.

   Public API: window.CbgCartSync
   ========================================================================== */
(function () {
  'use strict';

  /* Dawn's cart icon section — and, by design, the id of the header anchor it
     renders into. sections/cart-icon-bubble.liquid emits exactly the inner markup
     of that anchor. */
  var BUBBLE_ID = 'cart-icon-bubble';

  /* Mount point for the mini-cart dropdown body, inside the header section. */
  var PANEL_SELECTOR = '[data-cbg-minicart-content]';

  /* One in-flight fallback fetch at a time; a newer mutation supersedes an older one. */
  var inFlight = null;

  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* The header section id ("sections--123456__header"), read off the element that
     header.liquid renders with data-section-id="{{ section.id }}". Absent when the
     header is configured with a non-mega desktop menu — that layout renders a plain
     cart link with no dropdown, so only the counter needs syncing. */
  function headerSectionId() {
    var el = document.querySelector('cbg-mini-cart[data-section-id]');
    var id = el && el.getAttribute('data-section-id');
    return id || null;
  }

  function sectionIds() {
    var ids = [BUBBLE_ID];
    var header = headerSectionId();
    if (header) ids.push(header);
    return ids;
  }

  /* Value for the `sections` parameter of a Cart Ajax request, so the POST that adds
     the line already returns the re-rendered header markup. */
  function sectionsParam() {
    return sectionIds().join(',');
  }

  function parseHtml(html) {
    return new DOMParser().parseFromString(String(html), 'text/html');
  }

  /* Section Rendering API responses are wrapped in <div class="shopify-section">. */
  function sectionRoot(html) {
    var doc = parseHtml(html);
    return doc.querySelector('.shopify-section') || doc.body;
  }

  /* Counter. Like Dawn, we swap the anchor's children and keep the anchor itself, so
     its aria-* state survives — and so does the dropdown toggle, which is delegated
     on <cbg-mini-cart> rather than bound to the anchor. */
  function applyBubble(html) {
    var anchor = document.getElementById(BUBBLE_ID);
    if (!anchor || !html) return false;
    var fresh = sectionRoot(html);
    /* Guard against an error page / empty body overwriting a working icon. */
    if (!fresh || !fresh.querySelector('.cart-count-bubble')) return false;
    anchor.innerHTML = fresh.innerHTML;
    return true;
  }

  /* Dropdown body: line items, item count, subtotal, min-order-quantity guard, buttons. */
  function applyPanel(html) {
    var current = document.querySelector(PANEL_SELECTOR);
    if (!current || !html) return false;
    var fresh = sectionRoot(html).querySelector(PANEL_SELECTOR);
    if (!fresh) return false;
    current.replaceWith(fresh);
    return true;
  }

  /* Renders the header from a `sections` map. Returns true only when every target
     that exists on this page was updated — a partial result falls through to a
     fresh fetch rather than leaving a stale counter next to a fresh panel. */
  function applySections(sections) {
    if (!sections) return false;
    var ok = applyBubble(sections[BUBBLE_ID]);
    var header = headerSectionId();
    if (header) ok = applyPanel(sections[header]) && ok;
    return ok;
  }

  /* Fallback for mutations that did not ask for `sections`: request the same ids for
     the current URL. Returns a promise so callers can await the repaint. */
  function refresh() {
    var url;
    try {
      var parsed = new URL(window.location.href);
      parsed.searchParams.set('sections', sectionsParam());
      url = parsed.toString();
    } catch (e) {
      url = shopRoot() + '?sections=' + encodeURIComponent(sectionsParam());
    }

    if (inFlight) inFlight.abort();
    inFlight = typeof AbortController === 'function' ? new AbortController() : null;
    var controller = inFlight;

    return fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (response) {
        return response.ok ? response.json() : Promise.reject(response.status);
      })
      .then(function (sections) {
        if (controller && controller.signal.aborted) return false;
        return applySections(sections);
      })
      .catch(function () {
        /* A stale header is acceptable; the next navigation re-renders it server-side. */
        return false;
      })
      .finally(function () {
        if (inFlight === controller) inFlight = null;
      });
  }

  /* Hand off to Dawn's existing subscribers. `cbgHeaderSynced` tells our own
     subscriber below that the header was already rendered from this payload, so it
     does not fire a redundant second fetch. */
  function publishUpdate(source, cartData) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: source,
          cartData: cartData,
          cbgHeaderSynced: true,
        });
      }
    } catch (e) {
      /* pub/sub unavailable — the header above is already up to date. */
    }
  }

  /* Entry point for the CBG add-to-cart paths. Pass the parsed /cart/add response;
     include sectionsParam() in the request to get the single-round-trip path. */
  function handleCartResponse(source, data) {
    var applied = false;
    try {
      applied = applySections(data && data.sections);
    } catch (e) {
      applied = false;
    }
    if (!applied) refresh();
    publishUpdate(source, data);
    return applied;
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.cbgHeaderSynced) return;
      refresh();
    });
  }

  window.CbgCartSync = {
    sectionIds: sectionIds,
    sectionsParam: sectionsParam,
    applySections: applySections,
    refresh: refresh,
    handleCartResponse: handleCartResponse,
  };
})();
